// app/frontend/components/Modals/PlatformDonationModal.tsx
import React, { useState, useEffect } from 'react';
import { Stack, Group, Paper, Divider, Image, Alert } from '@mantine/core';
import { ThemedText } from '../Typography';
import { ThemedNumberInput, ThemedLoader, ThemedModal, ThemedTextInput, ThemedTextarea, ThemedButton } from '../ui';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { IconCheck, IconX, IconHeart } from '@tabler/icons-react';
import { loadStripe } from '@stripe/stripe-js/pure';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import artistStarIcon from '../../assets/images/money-star-icon.png';
import { COLORS } from '../../src/constants/theme';
import { starFees, type StarFeeBreakdown } from '../../src/utils/starFees';

interface User {
  id: number;
  username?: string;
  email?: string;
  name?: string;
}

interface PlatformDonationModalProps {
  opened: boolean;
  onClose: () => void;
  user: User | null;
  stripePublishableKey: string;
}

interface DonationFormData {
  stars: number;
  message: string;
  supporter_name: string;
  guest_email: string;
}

const PLATFORM_ARTIST_SLUG = 'wherecanwedance-platform';

// Payment form sub-component
function PaymentForm({
  user,
  onClose,
  formData,
  feeBreakdown,
  activeStars,
}: {
  user: User | null;
  onClose: () => void;
  formData: DonationFormData;
  feeBreakdown: StarFeeBreakdown;
  activeStars: number;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) {
      showNotification({
        color: 'red',
        title: 'Error',
        message: 'Stripe not loaded.',
        icon: <IconX />,
      });
      return;
    }
    setIsProcessing(true);

    try {
      // 1) Create a PaymentMethod
      const card = elements.getElement(CardElement)!;
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card,
        billing_details: {
          name: formData.supporter_name,
          email: user?.email || formData.guest_email,
        },
      });
      if (pmError) throw new Error(pmError.message);

      // 2) Send PM to server to create the PaymentIntent
      const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

      const res = await fetch(`/artists/${PLATFORM_ARTIST_SLUG}/create_payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrf,
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
        body: JSON.stringify({
          artist_slug: PLATFORM_ARTIST_SLUG,
          payment_method_id: paymentMethod.id,
          stars: formData.stars,
          message: formData.message,
          supporter_name: formData.supporter_name,
          guest_email: user ? undefined : formData.guest_email,
        }),
      });

      // Parse JSON even on errors
      let result: any = {};
      if ((res.headers.get('content-type') || '').includes('application/json')) {
        result = await res.json();
      }

      // If server returned a non-2xx, show its error message
      if (!res.ok) {
        showNotification({
          color: 'red',
          title: 'Payment Error',
          message: result.error || 'Payment failed, please try again.',
          icon: <IconX />,
        });
        return;
      }

      // 3) Confirm the PaymentIntent client-side (handles 3D Secure, etc.)
      const { client_secret } = result;
      const { error: confirmErr, paymentIntent } = await stripe.confirmCardPayment(client_secret);

      if (confirmErr) {
        showNotification({
          color: 'red',
          title: 'Authentication failed',
          message: confirmErr.message!,
          icon: <IconX />,
        });
        return;
      }

      // 4) Success!
      showNotification({
        color: 'green',
        title: 'Thank you for your support! 💖',
        message: "You've helped us keep WhereCanWeDance running and growing!",
        icon: <IconCheck />,
      });
      onClose();
      // Optionally redirect to a thank you page or refresh
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err: any) {
      showNotification({
        color: 'red',
        title: 'Oops!',
        message: err.message || 'Something went wrong.',
        icon: <IconX />,
      });
    } finally {
      setIsProcessing(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md" style={{ backgroundColor: '#1a1a1a', padding: '1rem' }}>
        <Paper p="md" style={{ backgroundColor: '#1f1f1f' }}>
          <ThemedText fw={500} mb={8}>
            Payment Info
          </ThemedText>
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#f2f2f2',
                  '::placeholder': { color: '#f2f2f2' },
                  lineHeight: '24px',
                },
              },
            }}
          />
        </Paper>
        <Paper p="md" style={{ backgroundColor: '#1f1f1f' }}>
          <ThemedText fw={500} mb={8}>
            Fee Breakdown
          </ThemedText>
          <Group justify="space-between">
            <ThemedText size="xs">Donation ({formData.stars}×$5)</ThemedText>
            <ThemedText>${feeBreakdown.baseCharge.toFixed(2)}</ThemedText>
          </Group>
          <Group justify="space-between">
            <ThemedText size="xs">{feeBreakdown.platformLabel}</ThemedText>
            <ThemedText>${feeBreakdown.platformFee.toFixed(2)}</ThemedText>
          </Group>
          <Group justify="space-between">
            <ThemedText size="xs">{feeBreakdown.stripeLabel}</ThemedText>
            <ThemedText mb={4}>${feeBreakdown.stripeFee.toFixed(2)}</ThemedText>
          </Group>
          <Divider />
          <Group justify="space-between">
            <ThemedText fw={500} mt={8}>
              Total
            </ThemedText>
            <ThemedText fw={600}>${feeBreakdown.total.toFixed(2)}</ThemedText>
          </Group>
        </Paper>
        <ThemedButton
          type="submit"
          fullWidth
          size="lg"
          loading={isProcessing}
          disabled={!stripe}
          className="poppins"
          onClick={handleSubmit}
          style={{
            background: !stripe ? '#666' : 'linear-gradient(135deg, #EA580C 0%, #C2410C 50%, #F97316 100%)',
            border: 'none',
            boxShadow: !stripe ? 'none' : '0 0 15px rgba(234, 88, 12, 0.4)',
            transition: 'all 0.3s ease',
            fontWeight: 600,
          }}
          onMouseEnter={(e) => {
            if (stripe && !isProcessing) {
              e.currentTarget.style.boxShadow = '0 0 25px rgba(234, 88, 12, 0.6)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }
          }}
          onMouseLeave={(e) => {
            if (stripe && !isProcessing) {
              e.currentTarget.style.boxShadow = '0 0 15px rgba(234, 88, 12, 0.4)';
              e.currentTarget.style.transform = 'translateY(0)';
            }
          }}
        >
          {isProcessing ? 'Processing...' : `Donate ${activeStars} Star${activeStars > 1 ? 's' : ''}!`}
        </ThemedButton>
      </Stack>
    </form>
  );
}

// Main platform donation modal
export default function PlatformDonationModal({
  opened,
  onClose,
  user,
  stripePublishableKey,
}: PlatformDonationModalProps) {
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);
  const [step, setStep] = useState<'form' | 'payment'>('form');
  const [selectedPreset, setSelectedPreset] = useState<number | null>(1);
  const [customStars, setCustomStars] = useState<number | null>(null);

  const form = useForm<DonationFormData>({
    initialValues: {
      stars: 1,
      message: '',
      supporter_name: user?.username || '',
      guest_email: '',
    },
    validate: {
      stars: (v) => (v < 1 ? 'At least 1 star' : null),
      supporter_name: (v) => (!v.trim() ? 'Name required' : null),
      guest_email: (v) => {
        if (!user) {
          if (!v || !v.trim()) {
            return 'Email is required';
          }
          // simple email regex
          const emailPattern = /^\S+@\S+\.\S+$/;
          if (!emailPattern.test(v.trim())) {
            return 'Enter a valid email address';
          }
        }
        return null;
      },
      message: (v) => {
        if (!v || !v.trim()) {
          return 'Message is required';
        }
        if (v.trim().length < 10) {
          return 'Message must be at least 10 characters';
        }
        if (v.length > 1500) {
          return 'Message too long (max 1,500 characters)';
        }
        return null;
      },
    },
  });

  // Load Stripe when the modal first opens: the footer renders this on every page.
  useEffect(() => {
    if (opened && !stripePromise && stripePublishableKey) setStripePromise(loadStripe(stripePublishableKey));
  }, [opened, stripePromise, stripePublishableKey]);

  const activeStars = customStars ?? selectedPreset ?? 1;
  const feeBreakdown = starFees(activeStars);

  const proceed = () => {
    form.setFieldValue('stars', activeStars);
    if (!form.validate().hasErrors) setStep('payment');
  };

  const closeAll = () => {
    setStep('form');
    form.reset();
    setSelectedPreset(1);
    setCustomStars(null);
    onClose();
  };

  return (
    <ThemedModal opened={opened} onClose={closeAll} title="Support WhereCanWeDance" size="lg" navbarOffset>
      {step === 'form' ? (
        <Stack p="20px 4px" gap="md">
          {/* Intro Alert */}
          <Alert
            color="#EA580C"
            icon={<IconHeart style={{ fill: '#EA580C' }} />}
            styles={{ message: { color: '#ffffff' } }}
          >
            Support the WhereCanWeDance platform! Your donation helps us maintain the site, add new features, and keep
            the global dance community connected. Each star costs $5.
          </Alert>

          {/* Star selector row */}
          <Group wrap="nowrap" mb="md" style={{ overflowX: 'auto' }} align="center" justify="center" gap="4px">
            <Image src={artistStarIcon} alt="Stars" className="star-icon" />

            <ThemedText size="md" className="multiply-text">
              ×
            </ThemedText>

            {[1, 5, 10].map((v) => (
              <ThemedButton
                key={v}
                p="4px 16px"
                variant={selectedPreset === v ? 'filled' : 'light'}
                color={selectedPreset === v ? '#EA580C' : 'gray'}
                onClick={() => {
                  setSelectedPreset(v);
                  setCustomStars(null);
                }}
                size="sm"
              >
                {v}
              </ThemedButton>
            ))}

            <ThemedNumberInput
              placeholder="25"
              min={1}
              hideControls
              size="sm"
              value={customStars ?? ''}
              onChange={(val) => {
                setCustomStars(typeof val === 'number' && !isNaN(val) ? val : null);
                setSelectedPreset(null);
              }}
              styles={{
                input: {
                  background: '#f2f2f220',
                  border: 'none',
                  fontWeight: 'bold',
                  color: '#f9f9f9',
                  textAlign: 'center',
                },
              }}
              style={{ width: '50px' }}
            />
          </Group>

          {/* Supporter info fields */}
          <ThemedTextInput placeholder="Your Name" label="Your Name" {...form.getInputProps('supporter_name')} />
          {!user && (
            <ThemedTextInput
              placeholder="Enter your email"
              label="Email"
              type="email"
              {...form.getInputProps('guest_email')}
            />
          )}
          <ThemedTextarea
            placeholder="Leave an encouraging message for the WCWD team!"
            label="Message"
            {...form.getInputProps('message')}
            minRows={3}
          />

          {/* Continue to Payment */}
          <ThemedButton
            fullWidth
            size="lg"
            onClick={proceed}
            className="poppins"
            style={{
              background: 'linear-gradient(135deg, #EA580C 0%, #C2410C 50%, #F97316 100%)',
              border: 'none',
              boxShadow: '0 0 15px rgba(234, 88, 12, 0.4)',
              transition: 'all 0.3s ease',
              fontWeight: 600,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 0 25px rgba(234, 88, 12, 0.6)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 0 15px rgba(234, 88, 12, 0.4)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Donate ${(customStars ?? selectedPreset ?? 1) * 5}.00
          </ThemedButton>
        </Stack>
      ) : stripePromise ? (
        <Elements stripe={stripePromise}>
          <PaymentForm
            user={user}
            onClose={closeAll}
            formData={form.values}
            feeBreakdown={feeBreakdown}
            activeStars={activeStars}
          />
        </Elements>
      ) : (
        <ThemedLoader />
      )}

      {step === 'payment' && (
        <Group mt="md">
          <ThemedButton variant="light" color="#EA580C" onClick={() => setStep('form')}>
            ← Back
          </ThemedButton>
        </Group>
      )}
    </ThemedModal>
  );
}
